import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import RailIcon, { hasRailIcon } from 'modules/Navigation/Rail/icons';

import * as Styled from '../styles';

const matchesPath = (pathname, path) => {
  if (!path) return false;
  if (pathname === path) return true;
  return pathname.startsWith(path === '/' ? path : `${path}/`);
};

const ChevronDown = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="m3 5.5 4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DrawerItem = ({ section, currentPathname, badge, onSelectSection, onSelectSubRoute }) => {
  const hasSubRoutes = Array.isArray(section.subRoutes) && section.subRoutes.length > 0;
  const isSectionActive = useMemo(() => {
    if (matchesPath(currentPathname, section.path)) return true;
    if (hasSubRoutes) {
      return section.subRoutes.some((sub) => matchesPath(currentPathname, sub.path));
    }
    return false;
  }, [section, currentPathname, hasSubRoutes]);

  const [expanded, setExpanded] = useState(isSectionActive && hasSubRoutes);

  const handleRowClick = useCallback(() => {
    if (hasSubRoutes) {
      setExpanded((prev) => !prev);
    } else {
      onSelectSection(section);
    }
  }, [hasSubRoutes, onSelectSection, section]);

  return (
    <li>
      <Styled.DrawerItemRow
        type="button"
        onClick={handleRowClick}
        aria-expanded={hasSubRoutes ? expanded : undefined}
        aria-current={isSectionActive && !hasSubRoutes ? 'page' : undefined}
        $active={isSectionActive}
      >
        {isSectionActive && <Styled.ActiveAccent aria-hidden />}
        {hasRailIcon(section.title) && (
          <Styled.DrawerItemIcon>
            <RailIcon name={section.title} />
          </Styled.DrawerItemIcon>
        )}
        <Styled.DrawerItemLabel>{section.title}</Styled.DrawerItemLabel>
        {badge != null && <Styled.DrawerItemBadge>{badge > 99 ? '99+' : badge}</Styled.DrawerItemBadge>}
        {hasSubRoutes && (
          <Styled.DrawerItemChevron $open={expanded}>
            <ChevronDown />
          </Styled.DrawerItemChevron>
        )}
      </Styled.DrawerItemRow>

      {hasSubRoutes && expanded && (
        <Styled.DrawerSubList>
          {section.subRoutes.map((sub) => {
            const subActive = matchesPath(currentPathname, sub.path);
            return (
              <li key={sub.id ?? sub.path ?? sub.title}>
                <Styled.DrawerSubRow
                  type="button"
                  onClick={() => onSelectSubRoute(sub)}
                  aria-current={subActive ? 'page' : undefined}
                  $active={subActive}
                >
                  {sub.title}
                </Styled.DrawerSubRow>
              </li>
            );
          })}
        </Styled.DrawerSubList>
      )}
    </li>
  );
};

DrawerItem.propTypes = {
  section: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    path: PropTypes.string,
    subRoutes: PropTypes.array,
  }).isRequired,
  currentPathname: PropTypes.string.isRequired,
  badge: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onSelectSection: PropTypes.func.isRequired,
  onSelectSubRoute: PropTypes.func.isRequired,
};

DrawerItem.defaultProps = {
  badge: null,
};

export default DrawerItem;
